import { useRealTimeSync } from '@/composables/useRealTimeSync.js';
import { isOfflineFlagEnabled } from '@/configs/offlineFeatureFlags.js';
import offlineDB, {
    applyPosCatalogVariantChange,
    dropCachedRefundSale,
    hasCachedBranchStock,
    MAX_TARGETED_STOCK_VARIANTS,
    refreshCachedRefundSale,
    removePosCatalogVariants,
    syncBranchStock,
    syncBranchStockVariants,
    syncPosServicesToIDB,
    syncReturnBranchesToIDB,
} from '@/indexedDB/offlineDB.js';
import {
    isTableInBackoff,
    syncAccounts,
    syncAutomotives,
    syncBranches,
    syncCustomers,
    syncDeliveryPersons,
    syncCashCommitments,
    syncExpenses,
    syncGatePasses,
    syncLoadForms,
    syncOrderRequests,
    syncPurchases,
    syncSales,
    syncShelfLocations,
    syncSuppliers,
    syncTransfers,
    syncVariantLocations,
    syncWorkers,
} from '@/services/offlineSyncService.js';
import { clearSearchCoverage } from '@/services/posSearchCoverage.js';

/**
 * Canonical realtime sync apply layer (Phase 2).
 * Never maps event action names directly to arbitrary Dexie methods.
 */

export const SYNCABLE_TABLES = new Set([
    'units',
    'sizes',
    'categories',
    'products',
    'types',
    'materials',
    'colors',
    'return_branches',
    'product_variants',
    'branches',
    'suppliers',
    'customers',
    'purchases',
    'sales',
    'transfers',
    'gate_passes',
    'wholesaler_order_requests',
    'expenses',
    'accounts',
    'automotives',
    'load_forms',
    'delivery_persons',
    'cash_commitments',
    'workers',
    'services',
    // Virtual table: no Dexie store. Invalidation forces branch_products refresh.
    'branch_stocks',
    // Shelf Map. Geometry and hints are separate streams: relabelling an aisle
    // and putting something away are different events, and a client that only
    // cares about one should not have to refetch the other.
    'shelf_locations',
    'variant_locations',
]);

const DETAIL_TABLE_FOR = {
    branches: 'branch_details',
    suppliers: 'supplier_details',
    customers: 'customer_details',
    purchases: 'purchase_details',
    sales: 'sales_details',
    transfers: 'transfer_details',
    gate_passes: 'gate_pass_details',
    wholesaler_order_requests: 'order_request_details',
    expenses: 'expense_details',
    accounts: 'account_details',
    automotives: 'automotive_details',
    load_forms: 'load_form_details',
    delivery_persons: 'delivery_person_details',
    workers: 'worker_details',
};
const CANONICAL_ACTIONS = new Set(['upsert', 'bulk_upsert', 'delete', 'bulk_delete', 'invalidate']);
const AUTHORITATIVE_REFRESH_TABLES = new Set([
    'branches',
    'suppliers',
    'customers',
    'purchases',
    'sales',
    'transfers',
    'gate_passes',
    'wholesaler_order_requests',
    'return_branches',
    'expenses',
    'accounts',
    'automotives',
    'load_forms',
    'delivery_persons',
    'cash_commitments',
    // A put-away on one device, or a relabelled aisle, has to reach the other
    // tills in the branch. Both arrive as an `invalidate` and are answered by a
    // refetch through the reader's own authorised endpoint, so a session that
    // may not see that branch is never handed its racks.
    'shelf_locations',
    'variant_locations',
    'workers',
    // Wholesale list with no watermark and no per-row upsert: the only thing a
    // change to it can say is "refetch", which is what an invalidate means.
    'services',
]);

/** Tables that are sync signals without a matching Dexie object store. */
const VIRTUAL_SYNC_TABLES = new Set(['branch_stocks']);

let subscribedUserId = null;
let channelRef = null;
let subscriptionGeneration = 0;
let applyChain = Promise.resolve();
const invalidationTimers = new Map();
const branchStockTimers = new Map();
/** Variants owed a targeted refresh per branch, accumulated across a burst. */
const pendingStockVariants = new Map();
let returnBranchesTimer = null;

function normalizePrimaryKey(value) {
    if (typeof value === 'string' && /^(0|[1-9]\d*)$/.test(value)) {
        const numeric = Number(value);
        if (Number.isSafeInteger(numeric)) return numeric;
    }
    return value;
}

function normalizeDeleteId(payload) {
    if (payload == null) return null;
    if (typeof payload === 'number' || typeof payload === 'string') return normalizePrimaryKey(payload);
    return normalizePrimaryKey(payload.id ?? payload.value ?? null);
}

function compareRevision(a, b) {
    const aNumber = typeof a === 'number' || (typeof a === 'string' && /^-?\d+(?:\.\d+)?$/.test(a)) ? Number(a) : NaN;
    const bNumber = typeof b === 'number' || (typeof b === 'string' && /^-?\d+(?:\.\d+)?$/.test(b)) ? Number(b) : NaN;
    if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) return aNumber - bNumber;

    const aDate = typeof a === 'string' ? Date.parse(a) : NaN;
    const bDate = typeof b === 'string' ? Date.parse(b) : NaN;
    if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) return aDate - bDate;

    return String(a).localeCompare(String(b));
}

function isNewerOrEqual(incoming, existing, eventRevision = null) {
    if (!existing || !incoming) return true;
    const a = eventRevision ?? incoming.entity_revision ?? incoming._entity_revision ?? incoming.updated_at;
    const b = existing.entity_revision ?? existing._entity_revision ?? existing.updated_at;
    if (!a || !b) return true;
    return compareRevision(a, b) >= 0;
}

export { isNewerOrEqual };

function recordChangesExisting(existing, incoming) {
    if (!existing) return false;
    return Object.keys(incoming)
        .filter((key) => !['id', '_cached_at', '_entity_revision'].includes(key))
        .some((key) => JSON.stringify(existing[key]) !== JSON.stringify(incoming[key]));
}

/**
 * Translate legacy OfflineSyncEvent shapes into canonical actions.
 */
export function normalizeSyncEvent(e) {
    if (!e || typeof e !== 'object') return null;

    if (e.schema_version === 1 && CANONICAL_ACTIONS.has(e.action)) {
        return {
            schema_version: 1,
            table: e.table,
            action: e.action,
            payload: e.payload,
            entity_id: e.entity_id,
            entity_revision: e.entity_revision,
            occurred_at: e.occurred_at,
        };
    }

    // Legacy action names
    const table = e.table;
    let action = e.action;
    let payload = e.payload;

    const legacyMap = {
        add: 'upsert',
        update: 'upsert',
        bulkAdd: 'bulk_upsert',
        bulkUpdate: 'bulk_upsert',
        bulkDelete: 'bulk_delete',
        delete: 'delete',
    };

    if (legacyMap[action]) {
        action = legacyMap[action];
    }

    // Legacy bulkUpdate sometimes sent bare ID arrays — treat as invalidate
    if (e.action === 'bulkUpdate' && Array.isArray(payload) && payload.every((x) => typeof x === 'number' || typeof x === 'string')) {
        action = 'invalidate';
        payload = null;
    }

    // Legacy bulkUpdate {key, changes}[] — expand to full-row puts when changes look like rows
    if (e.action === 'bulkUpdate' && Array.isArray(payload) && payload.every((item) => item?.key != null && item?.changes)) {
        payload = payload.map((item) => ({
            id: item.key,
            ...(typeof item.changes === 'object' ? item.changes : {}),
        }));
        action = 'bulk_upsert';
    }

    return {
        schema_version: 1,
        table,
        action,
        payload,
        entity_id: e.entity_id ?? normalizeDeleteId(payload),
        entity_revision: e.entity_revision ?? payload?.updated_at,
        occurred_at: e.occurred_at ?? new Date().toISOString(),
        _legacy: true,
    };
}

async function deleteRelatedDetail(table, id) {
    const detail = DETAIL_TABLE_FOR[table];
    if (!detail || id == null) return;
    try {
        if (offlineDB[detail]) {
            await offlineDB[detail].delete(id);
        }
    } catch (err) {
        console.warn('[offlineRealtime] detail delete failed', detail, id, err);
    }
}

async function bustBranchStockSyncMeta(branchId) {
    if (!offlineDB.offline_sync_meta || branchId == null || branchId === '') return;
    try {
        await offlineDB.offline_sync_meta.delete(`branch_products:${String(branchId)}`);
    } catch (err) {
        console.warn('[offlineRealtime] branch stock meta bust failed', branchId, err);
    }
}

/**
 * Force-refresh cached POS stock for a branch, or bust TTL so the next POS
 * visit cannot skip a stale cache while offline.
 */
export function scheduleBranchStockRefresh(branchId, generation = subscriptionGeneration) {
    const id = Number(branchId);
    if (!Number.isFinite(id) || id <= 0) return;

    const key = String(id);
    clearTimeout(branchStockTimers.get(key));
    branchStockTimers.set(
        key,
        setTimeout(async () => {
            branchStockTimers.delete(key);
            if (generation !== subscriptionGeneration) return;
            try {
                const result = await syncBranchStock(id, true);
                if (!result?.ok) {
                    await bustBranchStockSyncMeta(id);
                }
            } catch (err) {
                console.warn('[offlineRealtime] branch stock refresh failed', id, err);
                await bustBranchStockSyncMeta(id);
            }
        }, 300),
    );
}

/**
 * Debounced targeted refresh, one timer per branch.
 *
 * Variants accumulate across a burst rather than each event racing its own
 * request: a twenty-line checkout, or the same sale arriving twice because
 * `StockService::apply` and `ProcessDomainOutbox` both emit it, collapses into
 * one read naming every variant involved. Falls back to the full reconcile if
 * the burst grows past what one targeted read may ask for.
 */
export function scheduleBranchStockVariantRefresh(branchId, variantIds, generation = subscriptionGeneration) {
    const id = Number(branchId);
    if (!Number.isFinite(id) || id <= 0) return;

    const key = String(id);
    const pending = pendingStockVariants.get(key) ?? new Set();
    for (const variantId of variantIds) pending.add(variantId);
    pendingStockVariants.set(key, pending);

    clearTimeout(branchStockTimers.get(key));
    branchStockTimers.set(
        key,
        setTimeout(async () => {
            branchStockTimers.delete(key);
            const ids = [...(pendingStockVariants.get(key) ?? [])];
            pendingStockVariants.delete(key);
            if (generation !== subscriptionGeneration || ids.length === 0) return;

            try {
                const result = await syncBranchStockVariants(id, ids);
                if (result?.ok) return;
                // Not targetable, or the read failed. Fall back to the full
                // reconcile rather than leaving the named rows stale — busting
                // the marker first so a concurrent POS read cannot skip it.
                await bustBranchStockSyncMeta(id);
                scheduleBranchStockRefresh(id, generation);
            } catch (err) {
                console.warn('[offlineRealtime] targeted branch stock refresh failed', id, err);
                await bustBranchStockSyncMeta(id);
                scheduleBranchStockRefresh(id, generation);
            }
        }, 300),
    );
}

/**
 * Branch ids named by a branch_stocks invalidate. The server may address one
 * branch (a sale, a receiving) or several (a company price change touching
 * every branch that holds the variant).
 */
function branchStockEventBranchIds(e) {
    const raw = [...(Array.isArray(e.payload?.branch_ids) ? e.payload.branch_ids : []), e.payload?.branch_id, e.entity_id, e.payload?.id];

    const ids = [];
    for (const value of raw) {
        const id = Number(value);
        if (Number.isFinite(id) && id > 0 && !ids.includes(id)) ids.push(id);
    }
    return ids;
}

/**
 * The variants a `branch_stocks` invalidate named, if it named a workable few.
 *
 * `OfflineSyncJob::INVALIDATE_ROUTING_TABLES` whitelists `product_variant_ids`
 * onto this event precisely so the recipient can be surgical, and the client
 * threw it away — busting the branch's freshness marker and re-paginating the
 * whole catalogue to learn that three quantities had changed. Empty when the
 * server named none, or named more than a targeted read is cheaper than.
 */
function branchStockEventVariantIds(e) {
    const raw = e.payload?.product_variant_ids;
    if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_TARGETED_STOCK_VARIANTS) return [];

    const ids = [];
    for (const value of raw) {
        const id = Number(value);
        if (Number.isSafeInteger(id) && id > 0 && !ids.includes(id)) ids.push(id);
    }
    return ids;
}

async function applyBranchStocksEvent(e) {
    const branchIds = branchStockEventBranchIds(e);
    if (branchIds.length === 0) {
        return { ok: false, reason: 'bad_payload' };
    }

    // Only branches this session already cached are refreshed. A terminal that
    // never sells from a branch must not pull its whole catalogue because
    // someone else moved stock there, and the membership test has to run before
    // the TTL marker is deleted below.
    const cached = [];
    for (const branchId of branchIds) {
        if (await hasCachedBranchStock(branchId)) cached.push(branchId);
    }

    if (cached.length === 0) {
        return { ok: true, reason: 'branch_stock_not_cached', branch_ids: branchIds };
    }

    // The event named what moved, so ask for exactly that — one request whatever
    // the catalogue size, instead of ~60 paginated reads per branch per event.
    const variantIds = branchStockEventVariantIds(e);
    if (variantIds.length) {
        for (const branchId of cached) scheduleBranchStockVariantRefresh(branchId, variantIds);
        return {
            ok: true,
            reason: 'branch_stock_invalidate',
            branch_ids: cached,
            variant_ids: variantIds,
        };
    }

    for (const branchId of cached) {
        // Bust TTL first so a concurrent POS read cannot skip the refetch while
        // the debounced refresh below is still pending.
        await bustBranchStockSyncMeta(branchId);
        scheduleBranchStockRefresh(branchId);
    }

    return { ok: true, reason: 'branch_stock_invalidate', branch_ids: cached };
}

/**
 * Keep the POS search catalogue in step with a `product_variants` event.
 *
 * `pos_catalog` is a separate store from `product_variants` on purpose (see the
 * v27 schema note), so the switch below never touches it — a renamed, paused or
 * deleted variant would otherwise stay in local search results indefinitely.
 */
async function syncPosCatalogWithVariantEvent(e) {
    try {
        switch (e.action) {
            case 'upsert':
                if (e.payload?.id != null) await applyPosCatalogVariantChange(e.payload);
                break;
            case 'bulk_upsert':
                for (const row of Array.isArray(e.payload) ? e.payload : []) {
                    if (row?.id != null) await applyPosCatalogVariantChange(row);
                }
                break;
            case 'delete': {
                const id = normalizeDeleteId(e.payload) ?? normalizePrimaryKey(e.entity_id);
                if (id != null) await removePosCatalogVariants([id]);
                break;
            }
            case 'bulk_delete':
                await removePosCatalogVariants((Array.isArray(e.payload) ? e.payload : []).map(normalizeDeleteId).filter((id) => id != null));
                break;
            default:
                break;
        }
    } catch (err) {
        console.warn('[offlineRealtime] pos catalog sync failed', e.action, err);
    }
}

/**
 * Sales named by an event, in whatever shape it arrived: a full row for the
 * actor, a whitelisted routing id for everyone else, an id list for a bulk
 * delete.
 */
function saleEventIdentities(e) {
    if (Array.isArray(e.payload)) {
        return e.payload
            .map((item) => (item && typeof item === 'object' ? item : { id: item }))
            .map((row) => ({ id: row?.id ?? null, salesOrder: row?.sales_order ?? null }))
            .filter((row) => row.id != null || row.salesOrder != null);
    }

    const id = e.payload?.id ?? e.entity_id ?? null;
    const salesOrder = e.payload?.sales_order ?? null;
    return id == null && salesOrder == null ? [] : [{ id, salesOrder }];
}

/**
 * Keep the refund sale cache honest when the sale behind it moves.
 *
 * `sale_details` / `sale_items` / `sale_payments` are keyed by sales_order and
 * hold the server's canonical refund allocation snapshot — remaining_qty,
 * refunded_amount, tax. `deleteRelatedDetail` does not reach them (it maps
 * `sales` to the sale *view* detail), so a refund, cancellation or exchange
 * elsewhere would leave this session quoting pre-refund quantities.
 *
 * Only sales this session already cached are touched.
 */
async function syncRefundSaleCacheWithSaleEvent(e) {
    const identities = saleEventIdentities(e);
    if (identities.length === 0) return;

    const removing = e.action === 'delete' || e.action === 'bulk_delete';
    for (const identity of identities) {
        try {
            await (removing ? dropCachedRefundSale(identity) : refreshCachedRefundSale(identity));
        } catch (err) {
            console.warn('[offlineRealtime] refund sale cache sync failed', identity, err);
        }
    }
}

/**
 * The allowed-return-branch list is derived from `branches` — the capability
 * flags plus the caller's own receive-stock access — and nothing on the server
 * emits it as a table of its own, so it is the one whitelisted store with no
 * emitter. Refresh it whenever its source changes.
 *
 * Debounced separately from `scheduleAuthoritativeSync`, which only runs for
 * invalidates and deletes: the person who just flipped `can_receive_stock`
 * receives an *upsert*, and they are exactly who must see the new list.
 *
 * Only a session that already holds the list refreshes it. A user without
 * refund access would otherwise collect 403s from an endpoint they never needed.
 */
export function scheduleReturnBranchesRefresh(generation = subscriptionGeneration) {
    clearTimeout(returnBranchesTimer);
    returnBranchesTimer = setTimeout(async () => {
        returnBranchesTimer = null;
        if (generation !== subscriptionGeneration) return;
        try {
            if (!offlineDB.return_branches) return;
            if ((await offlineDB.return_branches.count()) === 0) return;
            await syncReturnBranchesToIDB();
        } catch (err) {
            console.warn('[offlineRealtime] return branches refresh failed', err);
        }
    }, 300);
}

function scheduleAuthoritativeSync(table, generation) {
    if (!AUTHORITATIVE_REFRESH_TABLES.has(table)) return;

    clearTimeout(invalidationTimers.get(table));
    invalidationTimers.set(
        table,
        setTimeout(async () => {
            invalidationTimers.delete(table);
            if (generation !== subscriptionGeneration) return;
            try {
                if (table === 'return_branches') {
                    await syncReturnBranchesToIDB();
                    return;
                }
                if (table === 'services') {
                    await syncPosServicesToIDB();
                    return;
                }

                // An invalidate arrives per mutation, and the change-feed replay
                // turns every upsert into one. A list endpoint that is answering
                // 503 therefore got re-asked once per event on a busy shop — the
                // failure ladder was being written and never read on this path.
                // The table stays eligible for the next natural trigger; it just
                // stops being retried by somebody else's checkout.
                if (await isTableInBackoff(table)) return;

                const runners = {
                    branches: () => syncBranches(),
                    suppliers: () => syncSuppliers(),
                    customers: () => syncCustomers(),
                    purchases: () => syncPurchases(),
                    sales: () => syncSales(),
                    transfers: () => syncTransfers(),
                    gate_passes: () => syncGatePasses(),
                    wholesaler_order_requests: () => syncOrderRequests(),
                    expenses: () => syncExpenses(),
                    accounts: () => syncAccounts(),
                    automotives: () => syncAutomotives(),
                    load_forms: () => syncLoadForms(),
                    delivery_persons: () => syncDeliveryPersons(),
                    cash_commitments: () => syncCashCommitments(),
                    shelf_locations: () => syncShelfLocations(),
                    variant_locations: () => syncVariantLocations(),
                    workers: () => syncWorkers(),
                };
                await runners[table]?.();
            } catch (err) {
                console.warn('[offlineRealtime] authoritative refresh failed', table, err);
            }
        }, 300),
    );
}

export function shouldScheduleAuthoritativeRefresh(event, result) {
    if (!result?.ok || !event?.table) return false;
    // branch_stocks refresh is scheduled inline in applyOfflineSyncEvent.
    if (event.table === 'branch_stocks' || VIRTUAL_SYNC_TABLES.has(event.table)) return false;
    if (result.reason === 'invalidate' || event.action === 'invalidate') return true;
    return AUTHORITATIVE_REFRESH_TABLES.has(event.table) && (event.action === 'delete' || event.action === 'bulk_delete');
}

/**
 * Apply one canonical (or legacy-normalized) sync event.
 */
export async function applyOfflineSyncEvent(rawEvent) {
    if (!isOfflineFlagEnabled('safeRealtimeApply')) {
        return { ok: false, reason: 'flag_disabled' };
    }

    const e = normalizeSyncEvent(rawEvent);
    if (!e?.table || !e?.action) {
        console.warn('[offlineRealtime] malformed event', rawEvent);
        return { ok: false, reason: 'malformed' };
    }

    if (!SYNCABLE_TABLES.has(e.table)) {
        console.warn('[offlineRealtime] unknown table ignored:', e.table);
        return { ok: false, reason: 'unknown_table' };
    }

    if (e.table === 'branch_stocks') {
        return applyBranchStocksEvent(e);
    }

    // A variant was created, renamed or removed, so any "this keyword is fully
    // cached" conclusion the POS search made may no longer hold — and the
    // catalogue row backing local search has to move with it.
    if (e.table === 'product_variants') {
        clearSearchCoverage();
        await syncPosCatalogWithVariantEvent(e);
    }

    if (e.table === 'sales') {
        await syncRefundSaleCacheWithSaleEvent(e);
    }

    // return_branches is derived from branches and has no emitter of its own.
    if (e.table === 'branches') {
        scheduleReturnBranchesRefresh();
    }

    const table = offlineDB[e.table];
    if (!table) {
        console.warn('[offlineRealtime] store missing:', e.table);
        return { ok: false, reason: 'missing_store' };
    }

    try {
        switch (e.action) {
            case 'upsert': {
                const record = e.payload;
                if (!record || typeof record !== 'object' || record.id == null) {
                    return { ok: false, reason: 'bad_payload' };
                }
                const id = normalizePrimaryKey(record.id);
                const existing = await table.get(id);
                if (existing && !isNewerOrEqual(record, existing, e.entity_revision)) {
                    return { ok: true, reason: 'stale_ignored' };
                }
                const invalidatesDetail = !existing || recordChangesExisting(existing, record);
                await table.put({
                    ...existing,
                    ...record,
                    id,
                    _entity_revision: e.entity_revision ?? record.entity_revision ?? record._entity_revision ?? existing?._entity_revision,
                    _cached_at: new Date().toISOString(),
                });
                if (invalidatesDetail) await deleteRelatedDetail(e.table, id);
                return { ok: true };
            }
            case 'bulk_upsert': {
                if (!Array.isArray(e.payload)) return { ok: false, reason: 'bad_payload' };
                const rows = e.payload.filter((r) => r && typeof r === 'object' && r.id != null);
                if (!rows.length) return { ok: false, reason: 'empty' };
                const ids = rows.map((r) => normalizePrimaryKey(r.id));
                const existingRows = await table.bulkGet(ids);
                const cachedAt = new Date().toISOString();
                const changedIds = [];
                const stamped = rows.flatMap((r, index) => {
                    const existing = existingRows[index];
                    const revision = r.entity_revision ?? r._entity_revision ?? e.entity_revision;
                    if (existing && !isNewerOrEqual(r, existing, revision)) return [];
                    if (!existing || recordChangesExisting(existing, r)) changedIds.push(ids[index]);
                    return [
                        {
                            ...existing,
                            ...r,
                            id: ids[index],
                            _entity_revision: revision ?? existing?._entity_revision,
                            _cached_at: cachedAt,
                        },
                    ];
                });
                if (stamped.length) await table.bulkPut(stamped);
                if (changedIds.length) {
                    await Promise.all(changedIds.map((id) => deleteRelatedDetail(e.table, id)));
                }
                return { ok: true, count: stamped.length, stale: rows.length - stamped.length };
            }
            case 'delete': {
                const id = normalizeDeleteId(e.payload) ?? normalizePrimaryKey(e.entity_id);
                if (id == null) return { ok: false, reason: 'bad_payload' };
                const existing = await table.get(id);
                if (existing && e.entity_revision && !isNewerOrEqual({ id }, existing, e.entity_revision)) {
                    return { ok: true, reason: 'stale_ignored' };
                }
                await table.delete(id);
                await deleteRelatedDetail(e.table, id);
                return { ok: true };
            }
            case 'bulk_delete': {
                let ids = e.payload;
                if (!Array.isArray(ids)) return { ok: false, reason: 'bad_payload' };
                ids = ids.map(normalizeDeleteId).filter((id) => id != null);
                if (!ids.length) return { ok: false, reason: 'empty' };
                await table.bulkDelete(ids);
                await Promise.all(ids.map((id) => deleteRelatedDetail(e.table, id)));
                return { ok: true, count: ids.length };
            }
            case 'invalidate': {
                // Soft signal — DataTable / list sync listens via touchTable
                return { ok: true, reason: 'invalidate' };
            }
            default:
                console.warn('[offlineRealtime] unknown action ignored:', e.action);
                return { ok: false, reason: 'unknown_action' };
        }
    } catch (err) {
        console.error('[offlineRealtime] apply failed', e.table, e.action, err);
        return { ok: false, reason: 'exception', error: err };
    }
}

/**
 * Apply one event and schedule whatever follow-up refresh it implies.
 *
 * The Echo listener and the replayed change feed both land here, so a change
 * that arrived live and the same change replayed after an outage cannot take
 * different paths — which is the whole point of the feed. Keep it that way: a
 * second apply site is a second set of convergence bugs.
 *
 * Destructive events have no durable tombstone ordering and can arrive after a
 * recreate, so the debounced authoritative refresh makes scoped caches converge
 * even when the delete was stale or replayed.
 */
export async function handleSyncEvent(rawEvent, generation = subscriptionGeneration) {
    const event = normalizeSyncEvent(rawEvent);
    const result = await applyOfflineSyncEvent(event);

    if (shouldScheduleAuthoritativeRefresh(event, result)) {
        scheduleAuthoritativeSync(event.table, generation);
    }

    return result;
}

/**
 * Nudge the change feed after a live event.
 *
 * The socket delivers the change for an immediate UI response, but it must not
 * move the cursor. Ids are assigned at insert and published at commit, and a
 * rolled-back transaction leaves a permanent hole, so "advance when the event's
 * cursor is ours plus one" would either skip a change or stall forever on a gap
 * that is never filled. Letting the feed do the advancing keeps one authority;
 * the poll costs an empty response when the socket already applied everything,
 * and the apply is idempotent when it did not.
 *
 * The delay is thirty seconds rather than the second and a half it was, because
 * of what the poll is *for*. Nothing on screen waits for it — the socket already
 * applied the change — so its only job is to leave the cursor somewhere useful
 * for the next replay, and a replay reads a range just as cheaply as it reads
 * one row. At a second and a half a shop doing a sale a minute paid a request a
 * minute per terminal to learn nothing; the cursor being thirty seconds further
 * behind costs that same replay nothing at all.
 */
let feedCatchUpTimer = null;

export function scheduleFeedCatchUp(delayMs = 30_000) {
    if (!isOfflineFlagEnabled('changeFeedSync')) return;
    // A hidden tab has no screen to keep honest, and `convergeOfflineCaches`
    // replays the feed the moment it comes back — so polling here would buy
    // nothing that the resume does not already buy.
    if (typeof document !== 'undefined' && document.hidden) return;
    if (feedCatchUpTimer) clearTimeout(feedCatchUpTimer);

    feedCatchUpTimer = setTimeout(() => {
        feedCatchUpTimer = null;
        // Imported lazily: changeFeedService imports this module for the shared
        // apply function, and a static import here would close the cycle.
        import('@/services/changeFeedService.js')
            .then(({ runChangeFeedSync }) => runChangeFeedSync({ reason: 'realtime_event' }))
            .catch((error) => console.warn('[offlineRealtime] feed catch-up failed', error));
    }, delayMs);
}

export function cancelFeedCatchUp() {
    if (feedCatchUpTimer) {
        clearTimeout(feedCatchUpTimer);
        feedCatchUpTimer = null;
    }
}

/**
 * Screens that want the event itself, not just the fact that a table moved.
 *
 * `useRealTimeSync().touchTable()` records a timestamp per table, which is
 * everything a DataTable needs to refetch and nothing a live map needs: Stock
 * Atlas has to pulse *the branch this sale happened at*, and that identifier is
 * already on the wire — `branch_stocks` invalidations carry `branch_id` and
 * `product_variant_ids` to every live session
 * (OfflineSyncJob::INVALIDATE_ROUTING_TABLES).
 *
 * So this hands out the normalized event rather than adding a broadcast, a
 * channel or a queued job. Listeners are decoration: one that throws is logged
 * and skipped, and nothing in the cache pipeline depends on any of them
 * running.
 */
const eventListeners = new Set();

export function onOfflineSyncEvent(listener) {
    if (typeof listener !== 'function') return () => {};

    eventListeners.add(listener);

    return () => eventListeners.delete(listener);
}

function notifyEventListeners(event) {
    if (!event || eventListeners.size === 0) return;

    for (const listener of eventListeners) {
        try {
            listener(event);
        } catch (err) {
            console.warn('[offlineRealtime] event listener failed', err);
        }
    }
}

/**
 * Subscribe to Echo channel for a user. Leaves previous channel first.
 */
export function subscribeOfflineSync(userId) {
    if (!userId || typeof Echo === 'undefined') return;

    const normalizedUserId = String(userId);
    if (String(subscribedUserId) === normalizedUserId && channelRef) return;

    leaveOfflineSync();

    const { touchTable } = useRealTimeSync();
    const generation = ++subscriptionGeneration;
    subscribedUserId = normalizedUserId;
    channelRef = Echo.private('indexedDB.update.' + normalizedUserId);

    channelRef.listen('OfflineSyncEvent', (raw) => {
        applyChain = applyChain
            .catch(() => {})
            .then(async () => {
                if (generation !== subscriptionGeneration || subscribedUserId !== normalizedUserId) return;
                const event = normalizeSyncEvent(raw);
                if (event?.table) touchTable(event.table);
                notifyEventListeners(event);
                await handleSyncEvent(event, generation);
                // The socket showed the change; the feed still owns the cursor.
                scheduleFeedCatchUp();
            })
            .catch((err) => {
                console.error('[offlineRealtime] listener error', err);
            });
    });
}

export function leaveOfflineSync() {
    subscriptionGeneration += 1;
    cancelPendingAuthoritativeSyncs();
    if (subscribedUserId != null && typeof Echo !== 'undefined') {
        try {
            Echo.leave('indexedDB.update.' + subscribedUserId);
        } catch (err) {
            console.warn('[offlineRealtime] leave failed', err);
        }
    }
    subscribedUserId = null;
    channelRef = null;
    return applyChain.catch(() => {});
}

export function cancelPendingAuthoritativeSyncs() {
    // A pending catch-up belongs to the session that scheduled it; letting it
    // fire after logout would replay the feed for the departing user.
    cancelFeedCatchUp();
    for (const timer of invalidationTimers.values()) clearTimeout(timer);
    invalidationTimers.clear();
    for (const timer of branchStockTimers.values()) clearTimeout(timer);
    branchStockTimers.clear();
    pendingStockVariants.clear();
    clearTimeout(returnBranchesTimer);
    returnBranchesTimer = null;
}

export function getSubscribedUserId() {
    return subscribedUserId;
}

export default {
    applyOfflineSyncEvent,
    handleSyncEvent,
    subscribeOfflineSync,
    leaveOfflineSync,
    normalizeSyncEvent,
    onOfflineSyncEvent,
    SYNCABLE_TABLES,
};
